import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import useFundContext from '@/contexts/Fund/useFundContext';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { FundStatus } from '@/models/common/FundStatus';
import { ReactivationRequestStatusDtoIn } from '@/models/in/ReactivationRequestDtoIn';
import Button from '@/components/ui/Button';
import SendReactivationDocumentsDialog from './SendReactivationDocumentsDialog';

/**
 * HAT-1301: a frozen fund stays in the coordinator's fund list (like a closed one), so this is
 * where the coordinator learns why nothing can be requested on it and what gets it going again -
 * and sends the identity document that does. Renders nothing for any other status.
 */
const FrozenFundNotice = () => {
  const { t, i18n } = useTranslation();
  const { currentFund } = useFundContext();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const isFrozen = currentFund?.status === FundStatus.Frozen;
  const statusUrl = isFrozen && currentFund ? `funds/financial-data/${currentFund.id}/reactivation-request` : '';
  const { data: request } = useGetAuthApi<ReactivationRequestStatusDtoIn>(statusUrl);

  if (!isFrozen || !currentFund) {
    return null;
  }

  const isPending = request?.status === 'Pending';
  const isRejected = request?.status === 'Rejected';
  const sentAt = request?.submittedAt ? new Date(request.submittedAt).toLocaleDateString(i18n.language) : '';

  return (
    <div
      role="status"
      className="body-md-regular"
      style={{
        marginBottom: '20px',
        padding: '12px 16px',
        border: '1px solid #fcd34d',
        borderRadius: '12px',
        background: '#fffbeb',
        color: '#92400e',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <div>
        <strong style={{ marginInlineEnd: '6px' }}>{t('frozenFundNotice.title')}</strong>
        {t('frozenFundNotice.body')}
      </div>
      {isPending && <div>{t('frozenFundNotice.pending', { date: sentAt })}</div>}
      {isRejected && (
        <div>
          <strong>{t('frozenFundNotice.rejected')}</strong> {request?.reviewNote}
        </div>
      )}
      <div>
        <Button variant={isPending ? 'outline' : 'primary'} onClick={() => setIsDialogOpen(true)}>
          {isPending ? t('frozenFundNotice.resend') : t('frozenFundNotice.send')}
        </Button>
      </div>
      <SendReactivationDocumentsDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        fundId={currentFund.id}
        onSent={() => queryClient.invalidateQueries({ queryKey: [statusUrl] })}
      />
    </div>
  );
};

export default FrozenFundNotice;
